import { useEffect, useState, useCallback } from 'react'
import { Anchor, Plus, X, Trash2, RefreshCw, AlertTriangle, ExternalLink, Copy, Check } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Token {
  id: number
  label: string
  token_type: string
  location: string
  canarytokens_url: string | null
  webhook_secret: string | null
  notes: string
  deployed_by: string
  deployed_at: string
  last_tripped_at: string | null
  trip_count: number
  trip_history: any[]
  active: boolean
}

const TOKEN_TYPES = [
  'email', 'dns', 'web', 'aws_keys', 'docx', 'pdf', 'qrcode',
  'wireguard', 'mysql', 'cloned_site', 'slack_api_key', 'kubeconfig',
]

export default function CanaryTokenPage() {
  const [tokens, setTokens] = useState<Token[]>([])
  const [summary, setSummary] = useState<any>({})
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [filter, setFilter] = useState<'all' | 'tripped' | 'active'>('all')
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [form, setForm] = useState({
    label: '', token_type: 'email', location: '', canarytokens_url: '',
    webhook_secret: '', notes: '', deployed_by: '',
  })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/sec/canary-tokens', {
        params: filter === 'all' ? {} : filter === 'tripped' ? { tripped_only: true } : { active_only: true },
      })
      setTokens(r.data?.tokens || [])
      setSummary(r.data?.summary || {})
    } finally { setLoading(false) }
  }, [filter])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.label) return
    try {
      await api.post('/api/sec/canary-tokens', form)
      setShowForm(false)
      setForm({ label: '', token_type: 'email', location: '', canarytokens_url: '', webhook_secret: '', notes: '', deployed_by: '' })
      fetchAll()
    } catch {}
  }

  const remove = async (id: number) => {
    if (!confirm('Delete token record?')) return
    await api.delete(`/api/sec/canary-tokens/${id}`)
    fetchAll()
  }

  const copyWebhookUrl = async (token: Token) => {
    const base = `${window.location.origin}/api/sec/canary-trip/webhook?token_id=${token.id}`
    const url = token.webhook_secret ? `${base}&secret=${token.webhook_secret}` : base
    await navigator.clipboard.writeText(url)
    setCopiedId(token.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Anchor size={24} className="text-amber-light" /> Canarytoken Board
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Honeytokens that should never fire. Generate at <a href="https://canarytokens.org" target="_blank" rel="noreferrer" className="text-blue-light hover:underline">canarytokens.org</a>, register here, paste the trip webhook to auto-record alerts.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs"><Plus size={12} /> Register token</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Total tokens" value={summary.total || 0} tone="blue" />
        <Tile label="Active" value={summary.active || 0} tone="emerald" />
        <Tile label="Tripped (ever)" value={summary.tripped || 0} tone={summary.tripped > 0 ? 'amber' : 'emerald'} pulse={(summary.tripped || 0) > 0} />
        <Tile label="Tripped 7d" value={summary.tripped_7d || 0} tone={summary.tripped_7d > 0 ? 'rose' : 'emerald'} pulse={(summary.tripped_7d || 0) > 0} />
        <Tile label="Total trips" value={summary.total_trips || 0} tone="amber" />
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'active', 'tripped'] as const).map(f => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={clsx('badge text-[10px] px-2 py-1',
              filter === f ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
            {f}
          </button>
        ))}
      </div>

      {/* Add form */}
      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Register canarytoken</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder='Label e.g. "AWS keys in vault root"' className="input py-1.5 md:col-span-2" />
            <select value={form.token_type} onChange={e => setForm({ ...form, token_type: e.target.value })} className="input py-1.5">
              {TOKEN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input value={form.deployed_by} onChange={e => setForm({ ...form, deployed_by: e.target.value })} placeholder="Deployed by" className="input py-1.5" />
            <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Where deployed e.g. /var/www/admin/.aws/credentials" className="input py-1.5 md:col-span-2" />
            <input value={form.canarytokens_url} onChange={e => setForm({ ...form, canarytokens_url: e.target.value })} placeholder="Original canarytokens.org URL (optional)" className="input py-1.5" />
            <input value={form.webhook_secret} onChange={e => setForm({ ...form, webhook_secret: e.target.value })} placeholder="Webhook shared secret (optional, recommended)" className="input py-1.5" />
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Notes — what's tripping this means" className="input py-1.5 md:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      <div className="card p-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
              <th className="text-left py-2 px-2">Label</th>
              <th className="text-left py-2 px-2">Type</th>
              <th className="text-left py-2 px-2">Location</th>
              <th className="text-right py-2 px-2">Trips</th>
              <th className="text-left py-2 px-2">Last tripped</th>
              <th className="text-right py-2 px-2">Webhook</th>
              <th className="text-right py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {tokens.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-text-muted text-[11px] italic">
                No tokens registered yet — click <em>Register token</em>.
              </td></tr>
            )}
            {tokens.map(t => (
              <tr key={t.id} className={clsx('border-b border-border/50 hover:bg-surface-hover',
                t.last_tripped_at && 'bg-rose/5')}>
                <td className="py-1.5 px-2">
                  <div className="text-text-primary font-medium flex items-center gap-1">
                    {t.last_tripped_at && <AlertTriangle size={10} className="text-rose" />}
                    {t.label}
                  </div>
                  {t.deployed_by && <div className="text-[9px] text-text-muted">by {t.deployed_by}</div>}
                </td>
                <td className="py-1.5 px-2 font-mono text-[10px] text-text-muted">{t.token_type}</td>
                <td className="py-1.5 px-2 text-[10px] text-text-secondary truncate max-w-[260px]" title={t.location}>{t.location || '—'}</td>
                <td className={clsx('py-1.5 px-2 text-right font-mono font-bold', t.trip_count > 0 ? 'text-rose' : 'text-text-muted')}>
                  {t.trip_count}
                </td>
                <td className="py-1.5 px-2 text-[10px] text-text-muted">
                  {t.last_tripped_at ? new Date(t.last_tripped_at).toLocaleString() : '—'}
                </td>
                <td className="py-1.5 px-2 text-right">
                  <button type="button" onClick={() => copyWebhookUrl(t)}
                    className="text-blue-light hover:underline inline-flex items-center gap-1 text-[10px]">
                    {copiedId === t.id ? <Check size={10} className="text-emerald" /> : <Copy size={10} />}
                    {copiedId === t.id ? 'copied' : 'copy URL'}
                  </button>
                </td>
                <td className="py-1.5 px-2 text-right space-x-2">
                  {t.canarytokens_url && (
                    <a href={t.canarytokens_url} target="_blank" rel="noreferrer" className="text-blue-light"><ExternalLink size={10} /></a>
                  )}
                  <button type="button" onClick={() => remove(t.id)} className="text-text-muted hover:text-rose"><Trash2 size={11} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card p-3 text-[11px] text-text-secondary">
        <strong>Webhook setup:</strong> generate a token at canarytokens.org, register it here, copy the webhook URL above
        and paste it as the alert URL on canarytokens.org. Trips POST to <code className="font-mono">/api/sec/canary-trip/webhook</code>
        with the token id + (optional) shared secret as query params.
      </div>
    </div>
  )
}

function Tile({ label, value, tone, pulse }: { label: string; value: number; tone: string; pulse?: boolean }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t, pulse && 'animate-pulse')}>{value}</div>
    </div>
  )
}
